import React, { useMemo } from 'react';
import 'components/main/NavMenu.scss';
import { TreeNodeProps } from 'business/models/CustomModels';
import ETreeNodeTypes from 'enums/ETreeNodeTypes';
import { EPlateState } from 'enums/EPlatestate';
import RMSvgIcon from 'components/customComponents/CommonComponents/RMSvgIcon';
import { getTreeNodeImageProps } from 'utils/Global';
import { TreeNodeDTO } from 'business/models/Models';
import { CustomCheckBox } from 'components/input';
import { useRightClick } from 'components/rightClick/rightClickProvider';
import { EExtraFieldNames } from 'enums/EExtraFieldNames';
import { ClickableNode } from 'components/clickableNode';

export function loadImageIcon(node) {
  let image;
  if (node.type?.includes('Folder')) {
    image = require(`components/main/images/TreeControl/Folder.png`);
    return image;
  }

  if (node.type === ETreeNodeTypes.Project) {
    image = require(`components/main/images/TreeControl/Project.png`);
    return image;
  }

  if (node.type === ETreeNodeTypes.Projects) {
    image = require(`components/main/images/TreeControl/Projects.png`);
    return image;
  }

  if (node.type === ETreeNodeTypes.Screens) {
    image = require(`components/main/images/TreeControl/Screens.png`);
    return image;
  }
  if (node.type === ETreeNodeTypes.QuickAccess) {
    image = require(`components/main/images/TreeControl/QuickAccess.svg`);
    return image;
  }
  try {
    if (node.type === ETreeNodeTypes.Experiment) {
      if (
        node.extra.plateState === EPlateState.Designed ||
        node.extra.plateState === EPlateState.Dispensed
      ) {
        image = require(`components/main/images/${node.type}_${node.extra.state}.png`);
      } else {
        image = require(`components/main/images/${node.type}.png`);
      }
    } else {
      image = require(`components/main/images/${node.type}.png`);
    }
  } catch (e) {
    image = require('components/main/images/Projects.png');
    console.error(e);
  }
  return image;
}

const nodeTypes: string[] = [
  ETreeNodeTypes.Project,
  ETreeNodeTypes.ProjectsFolder,
  ETreeNodeTypes.Projects,
  ETreeNodeTypes.Imaging,
  ETreeNodeTypes.Screens,
  ETreeNodeTypes.Searches,
  ETreeNodeTypes.Setup,
  ETreeNodeTypes.CrystalTrackv4,
  ETreeNodeTypes.ProjectFolder,
  ETreeNodeTypes.ScreensFolder,
  ETreeNodeTypes.QuickAccess,
  ETreeNodeTypes.SynchrotronTripv4Folder,
  ETreeNodeTypes.SearchesFolder,
];

const isSelectedNode = (selectedNode: any, currentNodeId: any) => {
  if (typeof selectedNode === 'string') {
    return selectedNode === `node${currentNodeId}`;
  }

  return selectedNode?.id === currentNodeId;
};

export default function TreeNode(props: TreeNodeProps) {
  const node = props.data;
  const { draggable, onDragStart, onDoubleClick = () => {} } = props;
  const { handleRightClick } = useRightClick();
  const onContextMenuHandler = (e: React.MouseEvent) => {
    props.onClickNodeChildren(props.data);
    if (!(node as TreeNodeDTO).locked) {
      props.openDocument();
    }
    handleRightClick(e);
  };

  const isValidNodeType = useMemo(() => nodeTypes.includes(node.type), [node.type]);

  return (
    <div
      title={node.name}
      draggable={draggable}
      onDragStart={onDragStart}
      id={`node${props.data.id}`}
      data-testid={props.data.id}
      className="explorer-nodes"
    >
      <div
        id={`tree-node-${node.name}-container`}
        className={`nav-link text-dark font-italic ${
          isSelectedNode(props.selectedNode, props.data.id)
            ? 'selectedNode'
            : `bg-[${props.backgroundColor}]`
        }`}
        style={{ display: 'flex', alignItems: 'center' }}
      >
        {isValidNodeType &&
        !node?.extra?.quickAccessMeta?.originalId &&
        ((node as TreeNodeDTO).hasContainerNode ||
          (node as TreeNodeDTO).extra?.[EExtraFieldNames.ShowExpandArrow]) ? (
          <div
            id={`tree-node-${node.name}-toogle`}
            style={{ display: 'inline-Block', cursor: 'pointer' }}
            onClick={() => {
              props.openDocument(false);
            }}
            onDoubleClick={() => onDoubleClick(node)}
          >
            <RMSvgIcon
              id={`tree-node-${node.name}-toogle-icon-arrow-down`}
              height={16}
              width={16}
              name="ArrowDown"
              fill={node.name === 'SynchrotronTrips' ? 'transparent' : ''}
              style={node?.state.expanded ? {} : { transform: 'rotate(-90deg)' }}
            />
          </div>
        ) : (
          <div
            id={`tree-node-${node.name}-toogle-style`}
            style={{ minWidth: 16, minHeight: 16 }}
          />
        )}
        <ClickableNode
          id={`tree-node-menu-handler-${node.name}`}
          onSingleClick={() => {
            props.onClickNodeChildren(props.data);
            if (!(node as TreeNodeDTO).locked) {
              props.openDocument();
            }
          }}
          onDoubleClick={() => onDoubleClick(node)}
          onContextMenu={onContextMenuHandler}
          style={{
            display: 'flex',
            alignItems: 'center',
            minWidth: props.onCheckboxChange ? 'calc(90% - 30px)' : '90%',
            maxWidth: props.onCheckboxChange ? 'calc(90% - 30px)' : '90%',
            marginLeft: '4%',
          }}
        >
          <div
            id={`tree-node-menu-${node.name}-icon-wrapper`}
            style={{ width: '10%', height: '100%' }}
          >
            <RMSvgIcon
              id={`tree-node-menu-image-${node.name}-icon`}
              {...getTreeNodeImageProps(node as TreeNodeDTO)}
            />
          </div>
          <div
            id={`tree-node-menu-${node.name}-title`}
            style={{
              marginLeft: '6px',
              width: '90%',
              height: '100%',
              overflowX: 'hidden',
              textOverflow: 'ellipsis',
              ...((node as TreeNodeDTO).locked && {
                textDecorationLine: 'line-through',
                textDecorationStyle: 'solid',
              }),
            }}
          >
            {node.name}
          </div>
        </ClickableNode>

        {props.onCheckboxChange && (
          <div
            id={`tree-node-checkboxchange-${node.name}-wrapper`}
            className="flex w-[30px] justify-center items-center h-full"
          >
            <CustomCheckBox
              id={`tree-node-checkbox-id-${node.id}`}
              onChange={() => props.onCheckboxChange(node.id)}
              disabled={props.checkboxDisabled}
              checked={
                props.treeControlState.checkedExplorerNodes.includes(node.id) &&
                !props.treeControlState.indeterminateExplorerNodes?.includes(node.id)
              }
              indeterminate={props.treeControlState.indeterminateExplorerNodes?.includes(
                node.id,
              )}
            />
          </div>
        )}
      </div>
    </div>
  );
}
